import currency from "currency.js";
import { useStoreState } from "easy-peasy";
import { useMemo } from "react";
import { useAuth } from "../../hooks/useAuth";
import { qtyFormat, qtyFormatToString } from "../../utils/helpers";
import { appSettings } from "../../config";

// This component is used to convert quantity based on UOM (Unit of Measurement)
// It takes in quantity, description, and a flag to convert now
// It also takes in uomId to find the correct UOM conversion
// If uomId is provided, it will convert the quantity based on the UOM conversion
// If not, it will format the quantity based on general settings

export default function ConvertQuantity({
  quantity = 0,
  desc = "",
  convertNow = false,

  // ==================
  // UOM FEATURE
  uomId = null,
  uomName = "",
  // ==================
}) {
  const { isIronRod } = useAuth();
  const generalSettings = useStoreState((state) => state?.generalSettings);
  const itemMeasurements = useStoreState((state) => state.itemMeasurements);
  const uomConversions = useStoreState((state) => state.uomConversions);

  const foundUOM = useMemo(() => {
    return uomConversions?.find((uom) => Number(uom.id) === Number(uomId));
  }, [uomConversions, uomId]);

  const commaQty = (value) => {
    return currency(value, {
      symbol: "",
      precision: 2,
    })
      .format()
      .toString()
      .replace(".00", "");
  };

  // NO QUANTITY
  if (Number(quantity) === 0) {
    return <>0</>;
  }

  // UOM CONVERSION
  if (uomId) {
    const baseQty = foundUOM?.baseQty || 1;
    const mainUomQty = Math.floor(quantity / baseQty);
    const remainingQty = quantity % baseQty;

    // NO NEED TO CONVERT WE HAVE THE UOM NAME
    if (uomName) {
      return (
        <>
          {commaQty(quantity)} {uomName}
        </>
      );
    }

    return (
      <>
        {quantity >= baseQty ? (
          <>
            {mainUomQty > 0 && (
              <>
                {commaQty(mainUomQty)} {foundUOM?.uom || "unit"}
                {commaQty(remainingQty) > 0 && " and "}
              </>
            )}
            {remainingQty > 0 && (
              <>
                {commaQty(remainingQty)} {foundUOM?.baseUom}
              </>
            )}
          </>
        ) : (
          <>
            {commaQty(quantity)} {foundUOM?.baseUom}
          </>
        )}
      </>
    );
  }

  return (
    <>
      {/* TONS AND PIECES */}
      {(isIronRod && generalSettings?.convertToTonsAndPcs) ||
      (convertNow && appSettings.isGigc)
        ? qtyFormatToString(
            qtyFormat(quantity, desc, itemMeasurements),
            false,
            generalSettings.convertableUOM
          )
        : // DEFAULT 1 is 1
          currency(quantity, {
            symbol: "",
            precision: 2,
          })
            .format()
            .toString()
            .replace(".00", "")}
    </>
  );
}

export function QuantityLabel() {
  const generalSettings = useStoreState((state) => state?.generalSettings);

  return generalSettings?.quantityLabel
    ? generalSettings?.quantityLabel
    : "Quantity";
}

export function useConvertQuantity() {
  const { isIronRod } = useAuth();
  const generalSettings = useStoreState((state) => state?.generalSettings);
  const itemMeasurements = useStoreState((state) => state.itemMeasurements);
  const uomConversions = useStoreState((state) => state.uomConversions);

  const convertQuantity = ({
    quantity = 0,
    desc = "",
    convertNow = false,
    uomId = null,
    uomName = "",
  }) => {
    const foundUOM = uomConversions?.find(
      (uom) => Number(uom.id) === Number(uomId)
    );

    const commaQty = (value) => {
      return currency(value, {
        symbol: "",
        precision: 2,
      })
        .format()
        .toString()
        .replace(".00", "");
    };

    if (uomId) {
      const baseQty = foundUOM?.baseQty || 1;
      const mainUomQty = Math.floor(quantity / baseQty);
      const remainingQty = quantity % baseQty;

      if (uomName) {
        return `${commaQty(quantity)} ${uomName}`;
      }

      if (quantity >= baseQty) {
        let result = "";
        if (mainUomQty > 0) {
          result += `${commaQty(mainUomQty)} ${foundUOM?.uom || "unit"}`;
          if (commaQty(remainingQty) > 0) {
            result += " and ";
          }
        }
        if (remainingQty > 0) {
          result += `${commaQty(remainingQty)} ${foundUOM?.baseUom}`;
        }
        return result;
      } else {
        return `${commaQty(quantity)} ${foundUOM?.baseUom}`;
      }
    }

    if (
      (isIronRod && generalSettings?.convertToTonsAndPcs) ||
      (convertNow && appSettings.isGigc)
    ) {
      return qtyFormatToString(
        qtyFormat(quantity, desc, itemMeasurements),
        false,
        generalSettings.convertableUOM
      );
    } else {
      return currency(quantity, {
        symbol: "",
        precision: 2,
      })
        .format()
        .toString()
        .replace(".00", "");
    }
  };

  return { convertQuantity };
}

export function formatQuantity({
  quantity = 0,
  desc = "",
  convertNow = false,
  uomId = null,
  uomName = "",
  uomConversions = [],
  itemMeasurements = [],
  generalSettings = {},
  isIronRod = false,
}) {
  const foundUOM = uomConversions?.find(
    (uom) => Number(uom.id) === Number(uomId)
  );

  const commaQty = (value) => {
    return currency(value, {
      symbol: "",
      precision: 2,
    })
      .format()
      .toString()
      .replace(".00", "");
  };

  // No quantity
  if (Number(quantity) === 0) {
    return "0";
  }

  // UOM conversion logic
  if (uomId) {
    const baseQty = foundUOM?.baseQty || 1;
    const mainUomQty = Math.floor(quantity / baseQty);
    const remainingQty = quantity % baseQty;

    if (uomName) {
      return `${commaQty(quantity)} ${uomName}`;
    }

    if (quantity >= baseQty) {
      let text = "";
      if (mainUomQty > 0) {
        text += `${commaQty(mainUomQty)} ${foundUOM?.uom || "unit"}`;
        if (commaQty(remainingQty) > 0) {
          text += " and ";
        }
      }
      if (remainingQty > 0) {
        text += `${commaQty(remainingQty)} ${foundUOM?.baseUom}`;
      }
      return text;
    } else {
      return `${commaQty(quantity)} ${foundUOM?.baseUom}`;
    }
  }

  // Default formatting
  if (
    (isIronRod && generalSettings?.convertToTonsAndPcs) ||
    (convertNow && appSettings.isGigc)
  ) {
    return qtyFormatToString(
      qtyFormat(quantity, desc, itemMeasurements),
      false,
      generalSettings.convertableUOM
    );
  }

  return currency(quantity, {
    symbol: "",
    precision: 2,
  })
    .format()
    .toString()
    .replace(".00", "");
}
